import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { SettingsPreviewResponse, Sizing } from '../../api';
import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { formatMoney } from '../../components/format-money';
import { reasonMessage } from '../../i18n';

import { numericInputSx, OptionRadio, SettingsSection } from './SettingsSection';
import { referenceLine } from './preview-reference';
import { previewFailure, type RiskPreviewState } from './risk-preview-state';
import {
  type DraftIssue,
  hasInvalid,
  lacksDigitBeforeSeparator,
  type SettingsDraft,
} from './settings-draft';

/** Props of {@link SizingSection}. */
interface SizingSectionProps {
  readonly draft: SettingsDraft;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
  readonly issues: readonly DraftIssue[];
  readonly preview: RiskPreviewState;
  readonly onRetryPreview: () => void;
}

/**
 * "Position size" card (M2 R3–R5, mockup 6): risk % listed first with the "Recommended" badge
 * but not selected (R2, I7), fixed lot second, and the money example of the preview.
 */
export function SizingSection({
  draft,
  onChange,
  issues,
  preview,
  onRetryPreview,
}: SizingSectionProps): ReactElement {
  const { t } = useTranslation();
  // A size the preview route refused (400) is marked like a size the form refuses (R20).
  const refused = preview.status === 'failed' && previewFailure(preview.error).marksSize;
  const riskInvalid = hasInvalid(issues, 'riskPercent') || refused;
  const lotsInvalid = hasInvalid(issues, 'lots') || refused;
  return (
    <SettingsSection title={t('settings.sizing.title')}>
      <RadioGroup
        aria-label={t('settings.sizing.title')}
        // An empty string selects nothing: no mode is pre-selected (R2).
        value={draft.sizingMode ?? ''}
        onChange={(event) => {
          const mode = event.target.value;
          if (mode === 'RISK_PERCENT' || mode === 'FIXED_LOT') onChange({ sizingMode: mode });
        }}
        sx={{ gap: 1.5 }}
      >
        <OptionRadio
          value="RISK_PERCENT"
          title={t('settings.sizing.riskPercent')}
          aside={
            <Box
              component="span"
              sx={{ ml: 1, fontSize: 12, fontWeight: 600, color: 'success.main' }}
            >
              {t('settings.sizing.recommended')}
            </Box>
          }
          hint={t('settings.sizing.riskHint')}
        />
        {draft.sizingMode === 'RISK_PERCENT' && (
          <TextField
            label={t('settings.sizing.riskLabel')}
            value={draft.riskPercent}
            onChange={(event) => {
              onChange({ riskPercent: event.target.value });
            }}
            error={riskInvalid}
            helperText={
              riskInvalid
                ? t('settings.errors.riskPercent')
                : lacksDigitBeforeSeparator(issues, 'riskPercent')
                  ? t('settings.errors.leadingDigit')
                  : undefined
            }
            slotProps={{ htmlInput: { inputMode: 'decimal', autoComplete: 'off' } }}
            sx={{ ...numericInputSx, ml: 4 }}
          />
        )}
        <OptionRadio
          value="FIXED_LOT"
          title={t('settings.sizing.fixedLot')}
          hint={t('settings.sizing.fixedLotHint')}
        />
        {draft.sizingMode === 'FIXED_LOT' && (
          <TextField
            label={t('settings.sizing.lotsLabel')}
            value={draft.lots}
            onChange={(event) => {
              onChange({ lots: event.target.value });
            }}
            error={lotsInvalid}
            helperText={
              lotsInvalid
                ? t('settings.errors.lots')
                : lacksDigitBeforeSeparator(issues, 'lots')
                  ? t('settings.errors.leadingDigit')
                  : undefined
            }
            slotProps={{ htmlInput: { inputMode: 'decimal', autoComplete: 'off' } }}
            sx={{ ...numericInputSx, ml: 4 }}
          />
        )}
      </RadioGroup>
      <PreviewBlock preview={preview} onRetry={onRetryPreview} />
    </SettingsSection>
  );
}

/** The money example (M2 R4, §4 "Preview shown" / "Preview unavailable"), announced politely. */
function PreviewBlock({
  preview,
  onRetry,
}: {
  readonly preview: RiskPreviewState;
  readonly onRetry: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  if (preview.status === 'failed') {
    // AC5: without a figure, saving is unavailable; the text says why (previewFailure).
    const failure = previewFailure(preview.error);
    const message = reasonMessage(failure.reasonCode, preview.error.params);
    return (
      <Alert
        severity="warning"
        action={
          failure.retry ? (
            <Button color="inherit" size="small" onClick={onRetry} sx={{ minHeight: 36 }}>
              {t('settings.retry')}
            </Button>
          ) : undefined
        }
      >
        {t(message.key, message.values)}
      </Alert>
    );
  }
  return (
    <Box
      aria-live="polite"
      sx={(theme) => ({
        bgcolor: theme.fx.page,
        borderRadius: '10px',
        p: 1.5,
        fontSize: 14,
        lineHeight: 1.5,
      })}
    >
      {preview.status === 'idle' && t('settings.preview.idle')}
      {preview.status === 'loading' && t('settings.preview.loading')}
      {preview.status === 'ready' && (
        <>
          <Trans
            i18nKey="settings.preview.text"
            values={{
              pips: preview.preview.stopLossPips,
              amount: formatMoney({ ...preview.preview.estimatedLoss, language }),
            }}
            components={{ strong: <Box component="strong" sx={{ fontFamily: NUMERIC_FONT }} /> }}
          />
          <ReferenceLineText preview={preview.preview} mode={preview.sizing.mode} />
        </>
      )}
    </Box>
  );
}

/**
 * Names the instrument of the example in both modes (P9 R1–R3). In risk % the amount does not
 * depend on it (M2 R5: the size adapts to the stop), and the line says so (P9 D3); naming it
 * still tells the trader the example is about this channel. In fixed lot the amount uses that
 * instrument's pip value.
 */
function ReferenceLineText({
  preview,
  mode,
}: {
  readonly preview: SettingsPreviewResponse;
  readonly mode: Sizing['mode'];
}): ReactElement {
  const { t } = useTranslation();
  const line = referenceLine(preview, mode);
  return (
    <Box component="span" sx={{ display: 'block', fontSize: 13, color: 'text.secondary' }}>
      {t(line.key, line.values)}
    </Box>
  );
}
